import { HostAgency, SimpleTravelAgent } from "@daytrip/legacy-models";
import type { SimpleUser } from "@daytrip/legacy-models";
import type { Country } from "@daytrip/legacy-models";
import type { RetrieveTravelAgentsInput, UserProfile } from "@daytrip/api";
import { isNotUndefinedOrNull } from "@daytrip/utils";
import autobind from "autobind-decorator";
import { action, computed, observable } from "mobx";
import { apiTrpcClient } from "@/trpc/api";

import { PageStore } from "../../stores/PageStore";

import type { TravelAgentsPageRouter } from "./TravelAgentsPageRouter";

export enum SelectedTravelAgentsActions {
    assignHostAgency = "assignHostAgency",
}

@autobind
export class TravelAgentsPageStore extends PageStore<TravelAgentsPageRouter, {}> {
    @observable
    public travelAgents?: Array<SimpleTravelAgent>;

    @observable
    public editedTravelAgentModel?: SimpleTravelAgent;

    @observable
    public travelAgentsCount: number = 0;

    @observable
    public owners?: Array<SimpleUser>;

    @observable
    public countries?: Array<Country>;

    @observable
    public hostAgencies?: HostAgency[];

    @observable
    public selectedTravelAgentIds: Array<string> = [];

    @observable
    public isAssignHostAgencyModalOpen: boolean = false;

    @observable
    public selectedTravelAgentsActionInProgress?: SelectedTravelAgentsActions = undefined;

    @observable
    public isMultipleSelectRevealed = false;

    @action
    public toggleMultipleSelect(): void {
        this.isMultipleSelectRevealed = !this.isMultipleSelectRevealed;
        this.selectedTravelAgentIds = [];
    }

    @action
    public toggleSelectAllVisibleTravelAgents(): void {
        if (this.travelAgents) {
            if (this.isAllVisibleTravelAgentsSelected) {
                this.selectedTravelAgentIds = [];
            } else {
                const selectedTravelAgentIds = this.travelAgents.map((travelAgent) => travelAgent.userId);
                this.selectedTravelAgentIds = selectedTravelAgentIds;
            }
        }
    }

    @computed
    public get isAllVisibleTravelAgentsSelected(): boolean {
        return (
            this.travelAgents?.length ===
            this.travelAgents?.filter((travelAgent) => this.selectedTravelAgentIds.includes(travelAgent.userId)).length
        );
    }

    @action
    public toggleTravelAgentSelect(travelAgentId: string): void {
        const exists = this.selectedTravelAgentIds.includes(travelAgentId);

        if (exists) {
            this.selectedTravelAgentIds = this.selectedTravelAgentIds.filter(
                (selectedtravelAgentId) => selectedtravelAgentId !== travelAgentId,
            );
        } else {
            this.selectedTravelAgentIds = [...this.selectedTravelAgentIds, travelAgentId];
        }
    }

    @action
    public async copySelectedTravelAgentIdsToClipboard() {
        navigator.clipboard.writeText(this.selectedTravelAgentIds.join(","));
    }

    @action.bound
    async assignHostAgencyToSelectedTravelAgents(hostAgencyId: string) {
        if (!this.selectedTravelAgentIds.length) {
            return;
        }

        this.selectedTravelAgentsActionInProgress = SelectedTravelAgentsActions.assignHostAgency;

        try {
            await apiTrpcClient.travelAgent.bulkUpdate.mutate({
                travelAgentIds: this.selectedTravelAgentIds,
                hostAgencyId,
            });
        } catch (error) {
            this.selectedTravelAgentsActionInProgress = undefined;
            throw error;
        }

        this.closeAssignHostAgencyModal();
        await this.fetchContent();
        this.selectedTravelAgentIds = [];
        this.selectedTravelAgentsActionInProgress = undefined;
    }

    @action
    public openAssignHostAgencyModal = () => {
        if (!this.authenticationStore.hasPermissions("User_Customer:Modify")) {
            return;
        }
        this.isAssignHostAgencyModalOpen = true;
    };

    @action
    public closeAssignHostAgencyModal = () => {
        this.resetAssignHostAgencyState();
    };

    @action
    public resetAssignHostAgencyState(): void {
        this.isAssignHostAgencyModalOpen = false;
        this.selectedTravelAgentsActionInProgress = undefined;
        this.selectedTravelAgentIds = [];
    }

    @action
    public async onFetchData() {
        await this.fetchContent();
    }

    @action
    public async fetchContent() {
        this.travelAgents = undefined;

        const options = {
            skip: this.pageRouter.skip,
            limit: this.pageRouter.limit,
            sortBy: this.pageRouter.sortBy,
            sortDirection: this.pageRouter.sortDirection,
            search: this.pageRouter.search,
            statusIn: this.pageRouter.statusIn,
            ownerIds: this.pageRouter.ownerIds,
            countryIds: this.pageRouter.countryIds,
            hostAgencyIds: this.pageRouter.hostAgencyIds,
            partnerBookingAvailable: this.pageRouter.partnerBookingAvailable,
            includeTravelAgentsWithoutCountry: this.pageRouter.includeTravelAgentsWithoutCountry,
            travelAgentCategory: this.pageRouter.travelAgentCategories,
        } as RetrieveTravelAgentsInput;

        [this.travelAgents, this.travelAgentsCount, this.owners, this.countries, this.hostAgencies] = await Promise.all(
            [
                apiTrpcClient.travelAgent.retrieveTravelAgents.query(options),
                apiTrpcClient.travelAgent.retrieveTravelAgentsCount.query(options),
                apiTrpcClient.travelAgent.retrieveTravelAgentsOwners.query(),
                this.rpcClient.content.retrieveCountries({}),
                this.rpcClient.hostAgency.retrieveHostAgencies(),
            ],
        );
    }

    @action
    public async approveTravelAgent(id: string) {
        await apiTrpcClient.travelAgent.approve.mutate({ userId: id });
        this.fetchContent();
    }

    @action
    public async declineTravelAgent(id: string, reason: string) {
        await apiTrpcClient.travelAgent.decline.mutate({ userId: id, declinationReason: reason });
        this.fetchContent();
    }

    @action
    public async deleteTravelAgent(id: string) {
        await apiTrpcClient.travelAgent.delete.mutate({ userId: id });
        this.fetchContent();
    }

    public loadHostAgencyOptions = async (query: string) => {
        const hostAgencies = await this.rpcClient.hostAgency.retrieveHostAgencies();

        return {
            options: hostAgencies
                .filter((hostAgency) => hostAgency.name.toLowerCase().includes(query.toLowerCase()))
                .map((hostAgency) => ({
                    value: hostAgency._id,
                    label: hostAgency.name,
                })),
        };
    };

    @computed
    public get isAnythingLoading(): boolean {
        return isNotUndefinedOrNull(this.selectedTravelAgentsActionInProgress);
    }

    public isDataFetched(): this is TravelAgentsPageStore & TravelAgentsPageStoreDataFetched {
        return isNotUndefinedOrNull(this.travelAgents) && isNotUndefinedOrNull(this.owners);
    }
}

export interface TravelAgentsPageStoreDataFetched {
    travelAgents: Array<UserProfile>;
    owners: Array<SimpleUser>;
}
